import React, { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  ClipboardDocumentListIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import MainLayout from '../../layouts/MainLayout';
import api from '../../services/api';
import userStore from '../../store/userStore';
import OnboardingChecklist from '../../components/onboarding/OnboardingChecklist';
import GestorWelcome from '../../components/onboarding/GestorWelcome';
import { TASK_TYPES } from '@claimup/shared/constants/assignments';

/**
 * «Hoy»: lo primero que ve el cliente (docs/ux-cliente-ar-plan.md, segunda tanda).
 *
 * No son métricas: son cosas que hacer, cada una un enlace a su lista. Usa los
 * contadores que ya existen, cada uno con su ámbito decidido por el servidor; el
 * que conteste 403 (por rol, o porque la empresa no tiene AR) no sale.
 */
const REPLY_TYPES = TASK_TYPES.filter(t => t.startsWith('reply')).join(',');

const pedir = async (url, leer) => {
  try {
    const { data } = await api.get(url);
    const n = leer(data);
    return typeof n === 'number' ? n : null;
  } catch {
    return null;
  }
};

const Tarjeta = ({ to, icon: Icon, numero, titulo, detalle }) => (
  <Link
    to={to}
    className='group flex items-start gap-4 rounded-lg border border-slate-200 bg-white p-5 hover:border-blue-300 hover:shadow-sm'
  >
    <Icon className='h-7 w-7 flex-none text-blue-600' aria-hidden='true' />
    <div>
      <p className='text-3xl font-semibold text-slate-800'>{numero}</p>
      <p className='font-medium text-slate-800 group-hover:text-blue-700'>{titulo}</p>
      <p className='text-sm text-slate-500'>{detalle}</p>
    </div>
  </Link>
);

const TodayPage = () => {
  const { user, token } = userStore();
  const esGestor = user?.role === 'gestor';
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    if (!token) return undefined;
    let active = true;
    Promise.all([
      api.get('/assignments/counts').then(
        ({ data }) => data,
        () => null
      ),
      pedir('/annotations/user-task-count', d => d?.count),
      pedir('/ar-review/review-queue', d => d?.data?.pending),
    ]).then(([gestion, tareas, propuestas]) => {
      if (!active) return;
      const num = n => (typeof n === 'number' ? n : null);
      setCounts({
        gestiones: num(esGestor ? gestion?.mine : gestion?.all),
        respuestas: num(gestion?.reply),
        tareas,
        propuestas,
      });
    });
    return () => {
      active = false;
    };
  }, [esGestor, token]);

  const tarjetas = counts
    ? [
        counts.respuestas !== null && {
          key: 'respuestas',
          to: `/assignments?status=pending&task_type=${REPLY_TYPES}`,
          icon: ChatBubbleLeftRightIcon,
          numero: counts.respuestas,
          titulo: 'Respuestas por atender',
          detalle: 'Deudores que han escrito o llamado y esperan contestación.',
        },
        counts.gestiones !== null && {
          key: 'gestiones',
          to: '/assignments?status=pending',
          icon: ClipboardDocumentListIcon,
          numero: counts.gestiones,
          titulo: esGestor ? 'Tus gestiones de hoy' : 'Gestiones de hoy',
          detalle: 'Expedientes con algo pendiente para hoy o atrasado.',
        },
        counts.propuestas !== null && {
          key: 'propuestas',
          to: '/ar-review',
          icon: SparklesIcon,
          numero: counts.propuestas,
          titulo: 'Propuestas de la IA por revisar',
          detalle: 'Mientras una espera, ese expediente no avanza.',
        },
        counts.tareas !== null && {
          key: 'tareas',
          to: '/tasks',
          icon: CheckCircleIcon,
          numero: counts.tareas,
          titulo: 'Tareas para ti',
          detalle: 'Lo que te han dejado en una anotación.',
        },
      ].filter(Boolean)
    : [];

  // Si no contesta ninguno, no es «nada pendiente»: es que no se ha podido saber.
  const sinDatos = counts && tarjetas.length === 0;

  const todoAlDia = counts && tarjetas.length > 0 && tarjetas.every(t => t.numero === 0);

  // Sin sesión, al login, como el resto de pantallas: aquí no hay nada que enseñar.
  if (!token) return <Navigate to='/login' replace />;

  return (
    <MainLayout>
      <div className='mb-6'>
        <h1 className='text-2xl font-semibold text-slate-900'>Hoy</h1>
        <p className='mt-1.5 text-sm text-slate-600'>Lo que tienes pendiente. Pulsa en cada cosa para ir a ella.</p>
      </div>

      <OnboardingChecklist />
      <GestorWelcome />

      {!counts && <p className='text-sm text-slate-500'>Cargando…</p>}

      {sinDatos && (
        <p className='text-sm text-slate-500'>No se ha podido cargar lo pendiente. Prueba a recargar la página.</p>
      )}

      {todoAlDia && (
        <p className='mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800'>
          Todo al día: no tienes nada pendiente.
        </p>
      )}

      <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
        {tarjetas.map(({ key, ...t }) => (
          <Tarjeta key={key} {...t} />
        ))}
      </div>
    </MainLayout>
  );
};

export default TodayPage;
